import { useLazyQuery } from '@apollo/client';
import { SEND_TALENTS_REPORT } from 'graphql/user';
import { useCurrentUser } from 'hooks/auth';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import {
  ReportPreparingModal,
  useOpenReportPreparingModal,
} from 'screens/company-user/dashboard/dashboard-v2/open-jobs';

import { Description } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  CurrencyEnum,
  DashboardAnalyticsItem,
  Maybe,
  Query,
} from '@libs/graphql-types';
import { isNil } from '@libs/helpers/common';
import { formatNumber } from '@libs/helpers/format';
import Button from '@libs/ui/components/button';
import { formatRate } from '@libs/ui/components/job/utils/common';

import DataBlock from '../data-block';

interface YourWorkforceProps {
  data: Maybe<DashboardAnalyticsItem>[];
  pendingTalents: number;
}

const getValue = (
  { value, slug }: DashboardAnalyticsItem,
  { currency = CurrencyEnum.Eur }: { currency?: CurrencyEnum },
) => {
  const num = isNil(value) ? 0 : +value?.toFixed(2);

  if (slug === 'average_rate') {
    // TODO: get currency from the company user
    return formatRate({
      min: num,
      currency,
    });
  }
  return formatNumber(num);
};

const useStyles = makeStyles((theme) => ({
  button: {
    color: theme.palette.warning.dark,
    borderColor: theme.palette.warning.dark,
    height: '30px',
    padding: '0 8px',
    marginLeft: 'auto',
  },
}));

const YourWorkforce = ({ data = [], pendingTalents }: YourWorkforceProps) => {
  const openReportPreparingModal = useOpenReportPreparingModal();
  const { getData } = useCurrentUser();
  const currency = getData()?.data?.currentCompanyUser?.company?.currency;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [getReport] = useLazyQuery<Query>(SEND_TALENTS_REPORT, {
    onCompleted: () => {
      openReportPreparingModal();
    },
    onError: () => {
      enqueueSnackbar(`Report was not sent`, {
        variant: 'error',
      });
    },
  });
  const onSend = useCallback(async () => {
    await getReport();
  }, [getReport]);

  return (
    <DataBlock
      fullHeight
      title="My Community"
      button={
        <Button
          variant="outlined"
          onClick={onSend}
          endIcon={<Description />}
          className={classes.button}
        >
          Report
        </Button>
      }
    >
      <Grid spacing={2} container>
        <Grid justifyContent="space-between" container item>
          <Grid item>Pending verification</Grid>
          <Grid item>{pendingTalents}</Grid>
        </Grid>
        {(data as DashboardAnalyticsItem[])
          .filter((i) => i.key !== 'Invites sent')
          .map((item) => (
            <Grid key={item?.key} justifyContent="space-between" container item>
              <Grid item>{item?.key}</Grid>
              <Grid item>{getValue(item, { currency })}</Grid>
            </Grid>
          ))}
      </Grid>
      <ReportPreparingModal />
    </DataBlock>
  );
};

export default YourWorkforce;
